import { useGlobal } from 'contexts';

export const getColumnHeaders = (rowIndex, lineItems, itemData) => {
   if (lineItems?.length === 0) return [];

   // Get the selected item for the active row
   const activeItem =
      rowIndex !== null
         ? itemData?.find((item) => item.id.toString() === lineItems[rowIndex]?.itemId?.toString())
         : null;

   // If no active row or no item selected, get headers from the first item with UQC data
   if (!activeItem) {
      const itemWithUQC = lineItems
         ?.map((lineItem) => itemData?.find((item) => item.id.toString() === lineItem?.itemId?.toString()))
         ?.find((item) => item?.uqc1Data || item?.uqc2Data || item?.uqc3Data);

      if (!itemWithUQC) {
         // Return default headers if no UQC data found
         return ['Qty 1', 'Qty 2', 'Qty 3'];
      }

      const headers = [];
      if (itemWithUQC.uqc1Data?.name) {
         headers.push(itemWithUQC.uqc1Data.name);
      } else {
         headers.push('Qty 1');
      }
      if (itemWithUQC.uqc2Data?.name) {
         headers.push(itemWithUQC.uqc2Data.name);
      } else {
         headers.push('Qty 2');
      }
      if (itemWithUQC.uqc3Data?.name) {
         headers.push(itemWithUQC.uqc3Data.name);
      } else {
         headers.push('Qty 3');
      }
      return headers;
   }

   // Get headers from the active item
   const headers = [];
   if (activeItem.uqc1Data?.name) {
      headers.push(activeItem.uqc1Data.name);
   } else {
      headers.push('Qty 1');
   }
   if (activeItem.uqc2Data?.name) {
      headers.push(activeItem.uqc2Data.name);
   } else {
      headers.push('Qty 2');
   }
   if (activeItem.uqc3Data?.name) {
      headers.push(activeItem.uqc3Data.name);
   } else {
      headers.push('Qty 3');
   }
   return headers;
};

// Utility function to validate and calculate rate per based on range
// const calculateRatePer = (amount, hsnData) => {
//   if (!amount || !hsnData) return { ratePer: 0, validRange: false };

//   for (const hsn of hsnData) {
//     for (const subHsn of hsn.subHsn) {
//       if (amount >= subHsn.fromRate && amount <= subHsn.toRate) {
//         return {
//           ratePer: amount,
//           validRange: true,
//           hsnDetails: {
//             hsn,
//             subHsn
//           }
//         };
//       }
//     }
//   }
//   return { ratePer: 0, validRange: false };
// };

// // Calculate line item totals with tax ratios
// const calculateLineItemTotals = (lineItem, hsnData) => {
//   const amount = (parseFloat(lineItem.salesprice1) || 0) * (parseFloat(lineItem.qty1) || 0);
//   const { ratePer, validRange, hsnDetails } = calculateRatePer(amount, hsnData);

//   if (!validRange || !hsnDetails) {
//     return {
//       ...lineItem,
//       amount,
//       ratePer: 0,
//       cgstAmount: 0,
//       sgstAmount: 0,
//       igstAmount: 0,
//       utgstAmount: 0,
//       totalTax: 0
//     };
//   }

//   const { ratio } = hsnDetails.subHsn;

//   // Calculate tax amounts based on ratio
//   const cgstAmount = amount * (ratio.cgst / 100);
//   const sgstAmount = amount * (ratio.sgst / 100);
//   const igstAmount = amount * (ratio.igst / 100);
//   const utgstAmount = amount * (ratio.utgst / 100);

//   // Calculate total tax
//   const totalTax = cgstAmount + sgstAmount + igstAmount + utgstAmount;

//   return {
//     ...lineItem,
//     amount,
//     ratePer,
//     cgstAmount,
//     sgstAmount,
//     igstAmount,
//     utgstAmount,
//     totalTax
//   };
// };

// // Calculate total taxable value
// const calculateTaxableValue = (lineItems, hsnData) => {
//   return lineItems.reduce((totals, item) => {
//     const itemTotals = calculateLineItemTotals(item, hsnData);

//     return {
//       totalAmount: totals.totalAmount + itemTotals.amount,
//       totalCGST: totals.totalCGST + itemTotals.cgstAmount,
//       totalSGST: totals.totalSGST + itemTotals.sgstAmount,
//       totalIGST: totals.totalIGST + itemTotals.igstAmount,
//       totalUTGST: totals.totalUTGST + itemTotals.utgstAmount,
//       totalTax: totals.totalTax + itemTotals.totalTax
//     };
//   }, {
//     totalAmount: 0,
//     totalCGST: 0,
//     totalSGST: 0,
//     totalIGST: 0,
//     totalUTGST: 0,
//     totalTax: 0
//   });
// };

// export {
//   calculateRatePer,
//   calculateLineItemTotals,
//   calculateTaxableValue
// };

/**
 * Calculate tax amounts and process line items for billing
 * @param {Object} data - The input data containing header, lineItem, and saleBillTerms
 * @param {Object} options - Additional options containing company, user, itemData, billTermData, daybookData
 * @returns {Object} - Processed data with calculated amounts
 */
function calculateTaxAmount(data, options = {}) {
   const { company = {}, user = {}, itemData = [], billTermData = [], daybookData = [] } = options;

   // Initialize totals
   let totals = {
      qty1: 0,
      qty2: 0,
      qty3: 0,
      basicAmount: 0,
      discount1: 0,
      discount2: 0,
      discount3: 0,
      netAmount: 0,
      taxAmount: 0,
      gstAmount: 0,
      taxableAmount: 0,
      netGstAmount: 0,
   };

   // Calculate initial totals from line items
   function calculateInitialTotals(lineItems) {
      if (!lineItems || !Array.isArray(lineItems)) return totals;

      return {
         qty1: lineItems.reduce((acc, item) => acc + Number(item?.qty1 || 0), 0),
         qty2: lineItems.reduce((acc, item) => acc + Number(item?.qty2 || 0), 0),
         qty3: lineItems.reduce((acc, item) => acc + Number(item?.qty3 || 0), 0),
         basicAmount: lineItems.reduce((acc, item) => acc + Number(item?.amount || 0), 0),
         discount1: lineItems.reduce((acc, item) => acc + Number(item?.addLess1Amount || 0), 0),
         discount2: lineItems.reduce((acc, item) => acc + Number(item?.addLess2Amount || 0), 0),
         discount3: lineItems.reduce((acc, item) => acc + Number(item?.addLess3Amount || 0), 0),
         netAmount: lineItems.reduce((acc, item) => acc + Number(item?.netAmount || 0), 0),
         taxAmount: lineItems.reduce((acc, item) => acc + Number(item?.taxAmount || 0), 0),
         gstAmount: lineItems.reduce((acc, item) => acc + Number(item?.gstAmount || 0), 0),
         netGstAmount: lineItems.reduce((acc, item) => acc + Number(item?.netGst || 0), 0),
         taxableAmount: lineItems.reduce((acc, item) => acc + Number(item?.taxableAmount || 0), 0),
      };
   }

   // Calculate bill terms (additions and deductions)
   function calculateBillTerms(saleBillTerms) {
      let additionTotal = 0;
      let deductionTotal = 0;

      if (!saleBillTerms || !Array.isArray(saleBillTerms)) {
         return { additionTotal, deductionTotal, billTerm: 0 };
      }

      saleBillTerms.forEach((term, index) => {
         const amount = parseFloat(term.amount) || 0;
         const termData = billTermData[index];

         if (!termData) return;

         switch (termData.signname) {
            case 'Subtraction':
               deductionTotal += Math.abs(amount);
               break;
            case 'Addition':
               additionTotal += Math.abs(amount);
               break;
            case 'Both':
               if (amount < 0) {
                  deductionTotal += Math.abs(amount);
               } else {
                  additionTotal += amount;
               }
               break;
            default:
               break;
         }
      });

      const billTerm = additionTotal - deductionTotal;
      return { additionTotal, deductionTotal, billTerm };
   }

   // Process individual line item
   function processLineItem(item, totalTaxableAmount, netAmount) {
      const { itemRate = 0, qty1 = 0, addLess1Percent = 0, addLess2Percent = 0, addLess3Percent = 0 } = item;

      let amount = itemRate * qty1;
      let netGst = 0;
      let gstPercent = 0;
      let subHSNId = null;

      // Calculate discounts
      let addLess1Amount = (amount * addLess1Percent) / 100;
      let base2 = amount - addLess1Amount;
      let addLess2Amount = (base2 * addLess2Percent) / 100;
      let base3 = base2 - addLess2Amount;
      let addLess3Amount = (base3 * addLess3Percent) / 100;

      let netItemAmount = +(amount - addLess1Amount - addLess2Amount - addLess3Amount).toFixed(2);
      let taxableAmount = netAmount > 0 ? (totalTaxableAmount * netItemAmount) / netAmount : netItemAmount;

      // GST Calculation
      const daybook = daybookData.find((db) => db?.id === data?.header?.daybookId);
      const itemInfo = itemData.find((i) => i?.id === item?.itemId);

      if (company?.setting?.applyGST && daybook?.applyGst && itemInfo) {
         let hsn;

         if (item.typeOfSupply === 1 || item.typeOfSupply === 2) {
            if (itemInfo?.hsnData?.subHsn?.length === 1) {
               hsn = itemInfo.hsnData.subHsn[0];
            } else if (itemInfo?.hsnData?.subHsn?.length > 1) {
               hsn = itemInfo.hsnData.subHsn.find((hsn) => itemRate >= hsn.fromRate && itemRate <= hsn.toRate);
            }
         }

         if (hsn) {
            gstPercent = hsn?.gst ? Number(hsn.gst) : 0;
            netGst = itemRate * qty1 * (hsn.gst / 100);
            subHSNId = hsn.id;
         }
      }

      return {
         ...item,
         amount: Number(amount.toFixed(2)),
         netAmount: Number(netItemAmount.toFixed(2)),
         taxableAmount: Number(taxableAmount.toFixed(2)),
         addLess1Amount: Number(addLess1Amount.toFixed(2)),
         addLess2Amount: Number(addLess2Amount.toFixed(2)),
         addLess3Amount: Number(addLess3Amount.toFixed(2)),
         gstPercent: gstPercent,
         netGst: Number(netGst.toFixed(2)),
         subHSNId: subHSNId,
         typeOfSupply: item.typeOfSupply,
      };
   }

   // Main calculation logic
   try {
      // Step 1: Calculate initial totals
      totals = calculateInitialTotals(data?.lineItem);

      // Step 2: Calculate bill terms
      const { additionTotal, deductionTotal, billTerm } = calculateBillTerms(data?.saleBillTerms);

      // Step 3: Calculate total taxable amount
      let netTotal = totals.netAmount + additionTotal - deductionTotal;
      const totalTaxableAmount = netTotal;

      // Step 4: Process line items with GST calculations
      const processedLineItems =
         data?.lineItem?.map((item) => processLineItem(item, totalTaxableAmount, totals.netAmount)) || [];

      // Step 5: Recalculate totals with processed line items
      const finalTotals = calculateInitialTotals(processedLineItems);
      const finalNetAmount = finalTotals.taxableAmount + finalTotals.netGstAmount;

      // Step 6: Prepare header data
      const header = {
         ...data.header,
         basicAmount: Number(finalTotals.netAmount),
         netAmount: Number(finalNetAmount),
         taxableAmount: finalTotals.taxableAmount || finalTotals.netAmount,
         additionAmount: Number(additionTotal.toFixed(2)),
         deductionAmount: Number(deductionTotal.toFixed(2)),
      };

      // Return processed data
      return {
         success: true,
         data: {
            header: header,
            lineItem: processedLineItems,
            billTerm: data.saleBillTerms || [],
         },
         totals: finalTotals,
         calculations: {
            billTerm,
            totalTaxableAmount,
            finalNetAmount,
            additionTotal,
            deductionTotal,
         },
      };
   } catch (error) {
      return {
         success: false,
         error: error.message,
         data: {
            header: data?.header || {},
            lineItem: data?.lineItem || [],
            billTerm: data?.saleBillTerms || [],
         },
      };
   }
}

export const splitPayload = (payload, nosOfChallan = 1) => {
   const result = [];

   // For each challan we need to create
   for (let i = 0; i < nosOfChallan; i++) {
      // Create a deep copy of the header
      const header = JSON.parse(JSON.stringify(payload.header));

      // Process each line item
      const lineItems = payload.lineItem?.map((lineItem) => {
         // Create a copy of the line item without takaDetails
         const newLineItem = { ...lineItem };
         delete newLineItem.takaDetails;

         // Calculate how many takaDetails items should be in this challan
         const totalTakaDetails = lineItem.takaDetails.length;
         const itemsPerChallan = Math.ceil(totalTakaDetails / nosOfChallan);
         const startIndex = i * itemsPerChallan;
         const endIndex = Math.min(startIndex + itemsPerChallan, totalTakaDetails);

         // Add the appropriate portion of takaDetails
         newLineItem.takaDetails = lineItem.takaDetails.slice(startIndex, endIndex);

         // return newLineItem.map((li) => {
         //    return {
         //       ...li,
         //       qty1: li?.takaDetails?.length,
         //       qty3: li?.takaDetails?.reduce((acc, item) => acc + Number(item?.quantity3 || 0), 0),
         //    };
         // });

         newLineItem.qty1 = newLineItem.takaDetails?.length;
         newLineItem.qty3 = newLineItem.takaDetails?.reduce((acc, item) => acc + Number(item?.quantity3 || 0), 0);

         return newLineItem;
      });

      // Add the new challan to the result
      result.push({
         header: header,
         lineItem: lineItems,
         purchaseBillTerms: [],
         payments: [],
      });
   }

   return result;
};

// const nosOfChallan = 2;
// const result = splitPayload(payload, nosOfChallan);
export default calculateTaxAmount;
